<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="promptlevel-editor q-pa-sm">
      <q-card-section class="row">
        <div class="text-h6">
          {{ titleText }}
        </div>
        <q-space />
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-form @submit="onSave">
        <q-card-section class="q-pt-none">
          <div class="row col-12 items-center">
            <q-input
              :model-value="promptLevelTemplate.templateName"
              outlined
              hide-bottom-space
              readonly
              :label="$t('programs.promptLevels.editPromptLevelTemplate.inputTemplateNameLabel')"
              class="col q-pr-sm"
            />
          </div>

          <info
            v-if="!readonly"
            type="info"
          >
            {{ $t('programs.promptLevels.customizePromptLevelTemplate.infoCustomizationLimitations') }}
          </info>
        </q-card-section>
        <q-card-section
          :class="{ 'q-pa-none': !visibleOrderedPromptLevels.length }"
        >
          <q-card
            v-for="promptLevel in visibleOrderedPromptLevels"
            :key="promptLevel.order"
            class="row q-pa-sm q-mb-md"
          >
            <div class="col row">
              <q-card-section class="col-xs-12 col-sm-6 row">
                <q-btn
                  icon="colorize"
                  size="sm"
                  disable
                  class="col-auto q-mr-sm"
                  :style="getPromptLevelStyles(promptLevel)"
                >
                  <q-popup-proxy
                    ref="colorPicker"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-color
                      :model-value="promptLevel.color"
                      no-header
                      default-view="palette"
                    />
                  </q-popup-proxy>
                </q-btn>
                <q-input
                  ref="promptLevel"
                  v-model="promptLevel.trialOutcome"
                  readonly
                  :label="$t('programs.promptLevels.editPromptLevelTemplate.inputPromptLevelLabel')"
                  class="col"
                />
              </q-card-section>
              <q-card-section class="col-xs-12 col-sm-6">
                <q-input
                  v-if="isNumericOutcome"
                  :model-value="promptLevel.score"
                  type="number"
                  min="0"
                  :max="promptLevelTemplate.outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.WEIGHTED ? 100 : null"
                  outlined
                  disable
                  hide-bottom-space
                  :label="scoreInputLabel"
                  input-class="text-right"
                />
                <q-toggle
                  v-else
                  v-model="promptLevel.score"
                  checked-icon="check"
                  disable
                  :false-value="0"
                  :true-value="100"
                  color="positive"
                  :label="$t(
                    'programs.promptLevels.editPromptLevelTemplate.inputSuccessfulOutcomeLabel'
                  )"
                  unchecked-icon="clear"
                />
              </q-card-section>
            </div>
            <q-card-section
              class="col-auto q-ma-none q-pa-none column row justify-center"
            >
              <div class="q-pb-xs">
                <q-btn
                  icon="keyboard_arrow_up"
                  :disable="readonly || promptLevel.order === 1"
                  size="sm"
                  flat
                  round
                  @click="onReorderPromptLevel(promptLevel, -1, true)"
                />
              </div>
              <div>
                <q-separator />
              </div>
              <div class="q-pt-xs">
                <q-btn
                  icon="keyboard_arrow_down"
                  :disable="readonly || promptLevel.order === visibleOrderedPromptLevels.length"
                  size="sm"
                  flat
                  round
                  @click="onReorderPromptLevel(promptLevel, +1, true)"
                />
              </div>
            </q-card-section>
            <q-card-section
              class="col-auto q-ma-none q-pa-none q-pl-sm column row justify-center"
            >
              <universal-tooltip
                :text="$t('programs.promptLevels.customizePromptLevelTemplate.tooltipHidePromptLevel')"
                :show-tooltip="!readonly"
              >
                <q-btn
                  icon="visibility"
                  size="sm"
                  flat
                  round
                  color="dark"
                  :disable="readonly"
                  @click="onHidePromptLevel(promptLevel)"
                />
              </universal-tooltip>
            </q-card-section>
          </q-card>
        </q-card-section>
        <q-expansion-item
          v-model="hiddenExpanded"
          :label="$t('programs.promptLevels.customizePromptLevelTemplate.textHiddenPromptLevels')"
          dense
          header-class="text-subtitle2"
          header-style="display: inline-flex;"
        >
          <q-card-section
            :class="{ 'q-pa-none': !hiddenOrderedPromptLevels.length }"
          >
            <q-card
              v-for="promptLevel in hiddenOrderedPromptLevels"
              :key="promptLevel.order"
              class="row q-pa-sm q-mb-md"
            >
              <div class="col row">
                <q-card-section class="col-xs-12 col-sm-6 row">
                  <q-btn
                    icon="colorize"
                    size="sm"
                    disable
                    class="col-auto q-mr-sm"
                    :style="getPromptLevelStyles(promptLevel)"
                  >
                    <q-popup-proxy
                      ref="colorPicker"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-color
                        :model-value="promptLevel.color"
                        no-header
                        default-view="palette"
                      />
                    </q-popup-proxy>
                  </q-btn>
                  <q-input
                    ref="promptLevel"
                    v-model="promptLevel.trialOutcome"
                    readonly
                    :label="$t('programs.promptLevels.editPromptLevelTemplate.inputPromptLevelLabel')"
                    class="col"
                  />
                </q-card-section>
                <q-card-section class="col-xs-12 col-sm-6">
                  <q-input
                    v-if="isNumericOutcome"
                    :model-value="promptLevel.score"
                    type="number"
                    min="0"
                    :max="promptLevelTemplate.outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.WEIGHTED ? 100 : null"
                    outlined
                    disable
                    hide-bottom-space
                    :label="scoreInputLabel"
                    input-class="text-right"
                  />
                  <q-toggle
                    v-else
                    v-model="promptLevel.score"
                    checked-icon="check"
                    disable
                    :false-value="0"
                    :true-value="100"
                    color="positive"
                    :label="$t(
                      'programs.promptLevels.editPromptLevelTemplate.inputSuccessfulOutcomeLabel'
                    )"
                    unchecked-icon="clear"
                  />
                </q-card-section>
              </div>
              <q-card-section
                class="col-auto q-ma-none q-pa-none column row justify-center"
              >
                <div class="q-pb-xs">
                  <q-btn
                    icon="keyboard_arrow_up"
                    :disable="readonly || promptLevel.order === 1"
                    size="sm"
                    flat
                    round
                    @click="onReorderPromptLevel(promptLevel, -1, false)"
                  />
                </div>
                <div>
                  <q-separator />
                </div>
                <div class="q-pt-xs">
                  <q-btn
                    icon="keyboard_arrow_down"
                    :disable="readonly || promptLevel.order === hiddenOrderedPromptLevels.length"
                    size="sm"
                    flat
                    round
                    @click="onReorderPromptLevel(promptLevel, +1, false)"
                  />
                </div>
              </q-card-section>
              <q-card-section
                class="col-auto q-ma-none q-pa-none q-pl-sm column row justify-center"
              >
                <universal-tooltip
                  :text="$t('programs.promptLevels.customizePromptLevelTemplate.tooltipShowPromptLevel')"
                  :show-tooltip="!readonly"
                >
                  <q-btn
                    icon="visibility_off"
                    size="sm"
                    flat
                    round
                    color="dark"
                    :disable="readonly"
                    @click="onShowPromptLevel(promptLevel)"
                  />
                </universal-tooltip>
              </q-card-section>
            </q-card>
          </q-card-section>
        </q-expansion-item>
        <q-card-actions align="right">
          <q-btn
            v-if="readonly"
            v-close-popup
            flat
            :label="$t('common.buttonClose')"
            color="primary"
          />
          <template v-else>
            <q-btn
              v-close-popup
              flat
              :label="$t('common.buttonCancel')"
              color="secondary"
            />
            <universal-tooltip
              :text="$t('programs.promptLevels.customizePromptLevelTemplate.tooltipSaveDisabled')"
              :show-tooltip="isSaveDisabled"
            >
              <q-btn
                flat
                :label="$t('common.buttonSave')"
                type="submit"
                color="primary"
                data-e2e="button-save"
                :disable="isSaveDisabled"
              />
            </universal-tooltip>
          </template>
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>
<script>
import Info from 'components/Info';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { sortBy } from 'lodash';
import { FADING_STRATEGIES, OUTCOME_MEASUREMENT_TYPES } from 'src/constants/prompt-level-template';
import Targets from 'src/mixins/Targets';
import { isNumericOutcome, isRatingScale } from 'src/utils/PromptLevelUtils';

export default {
  name: 'CustomizePromptLevelTemplate',
  components: { Info, UniversalTooltip },

  mixins: [Targets],

  props: {
    promptLevelTemplate: {
      type: Object,
      required: true,
    },
    program: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      touched: false,
      hiddenExpanded: false,
      editProgramPromptLevels: {
        promptLevelId: null,
        visiblePromptLevels: [],
        hiddenPromptLevels: [],
      },
    };
  },

  computed: {
    OUTCOME_MEASUREMENT_TYPES() {
      return OUTCOME_MEASUREMENT_TYPES;
    },
    titleText() {
      return this.readonly
        ? this.$t('programs.promptLevels.customizePromptLevelTemplate.textTitlePreview')
        : this.$t('programs.promptLevels.customizePromptLevelTemplate.textTitleCustomize');
    },

    isSaveDisabled() {
      return this.visibleOrderedPromptLevels.length === 0;
    },

    visibleOrderedPromptLevels() {
      const applicablePromptLevels = this.getApplicablePromptLevels(this.promptLevelTemplate);

      return sortBy(
        applicablePromptLevels.reduce((acc, pl) => {
          const editPromptLevel = this.editProgramPromptLevels.visiblePromptLevels.find(({ id }) => id === pl.id);

          if (editPromptLevel) {
            acc.push({
              ...pl,
              order: editPromptLevel.order,
            });
          }

          return acc;
        }, []),
        'order',
      );
    },

    hiddenOrderedPromptLevels() {
      const applicablePromptLevels = this.getApplicablePromptLevels(this.promptLevelTemplate);

      return sortBy(
        applicablePromptLevels.reduce((acc, pl) => {
          const editPromptLevel = this.editProgramPromptLevels.hiddenPromptLevels.find(({ id }) => id === pl.id);

          if (editPromptLevel) {
            acc.push({
              ...pl,
              order: editPromptLevel.order,
            });
          }

          return acc;
        }, []),
        'order',
      );
    },

    isNumericOutcome() {
      return isNumericOutcome(this.promptLevelTemplate?.outcomeMeasurement);
    }, // isNumericOutcome()

    scoreInputLabel() {
      return isRatingScale(this.promptLevelTemplate?.outcomeMeasurement)
        ? this.$t('programs.promptLevels.editPromptLevelTemplate.inputRatingValueLabel')
        : this.$t('programs.promptLevels.editPromptLevelTemplate.inputScoreLabel');
    }, // scoreInputLabel()

  },

  watch: {
    promptLevelTemplate: {
      immediate: true,
      deep: false,
      handler(newTpl, prevTpl) {
        if (prevTpl && newTpl?.id !== prevTpl?.id) {
          this.editProgramPromptLevels = {
            promptLevelId: null,
            visiblePromptLevels: [],
            hiddenPromptLevels: [],
          };
          this.initEditProgramPromptLevels(newTpl, this.program);
          return;
        }

        this.editProgramPromptLevels = this.normalizeEditProgramPromptLevels(
          newTpl,
          this.editProgramPromptLevels,
        );
      },
    },
    program: {
      immediate: true,
      deep: false,
      handler(newProgram, prevProgram) {
        if (prevProgram && newProgram?.id !== prevProgram?.id) {
          this.editProgramPromptLevels = {
            promptLevelId: null,
            visiblePromptLevels: [],
            hiddenPromptLevels: [],
          };
        }

        this.initEditProgramPromptLevels(this.promptLevelTemplate, newProgram);
      },
    },
  },

  methods: {
    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.initEditProgramPromptLevels(this.promptLevelTemplate, this.program);
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    getApplicablePromptLevels(promptLevelTemplate) {
      const hasPromptLevelFadingStrategy = [FADING_STRATEGIES.MTL, FADING_STRATEGIES.MTL_S].includes(
        promptLevelTemplate?.fadingStrategy,
      );
      const hideExcludedPromptLevels = hasPromptLevelFadingStrategy && promptLevelTemplate?.simpleFading;

      if (!hideExcludedPromptLevels) {
        return promptLevelTemplate?.promptLevels || [];
      }

      return promptLevelTemplate?.promptLevels?.filter((pl) => !pl.isNegativeFadeResponse) || [];
    },

    normalizeEditProgramPromptLevels(promptLevelTemplate, edit) {
      const applicablePromptLevels = this.getApplicablePromptLevels(promptLevelTemplate);
      const applicableIds = applicablePromptLevels.map(({ id }) => id);

      let visible = (edit?.visiblePromptLevels || []).filter(({ id }) => applicableIds.includes(id));
      let hidden = (edit?.hiddenPromptLevels || []).filter(({ id }) => applicableIds.includes(id));

      const usedIds = new Set([...visible.map(({ id }) => id), ...hidden.map(({ id }) => id)]);

      const newIds = applicableIds.filter((id) => !usedIds.has(id));

      const maxVisibleOrder = visible.reduce((m, x) => Math.max(m, x.order || 0), 0);
      visible = [
        ...visible,
        ...newIds.map((id, idx) => ({
          id,
          order: maxVisibleOrder + idx + 1,
        })),
      ];

      const resequence = (arr) => sortBy(arr, 'order').map((x, idx) => ({ ...x, order: idx + 1 }));

      visible = resequence(visible);
      hidden = resequence(hidden);

      return {
        promptLevelId: promptLevelTemplate?.id ?? null,
        visiblePromptLevels: visible,
        hiddenPromptLevels: hidden,
      };
    },

    initEditProgramPromptLevels(promptLevelTemplate, program) {
      const templateId = promptLevelTemplate?.id;
      const customized = program?.customizedPromptLevels;

      const canUseCustomized = !!customized
        && !!customized.promptLevelId
        && customized.promptLevelId === templateId;

      const baseEdit = canUseCustomized
        ? {
          promptLevelId: customized.promptLevelId,
          visiblePromptLevels: customized.visiblePromptLevels || [],
          hiddenPromptLevels: customized.hiddenPromptLevels || [],
        }
        : {
          promptLevelId: templateId,
          visiblePromptLevels: (this.getApplicablePromptLevels(promptLevelTemplate) || []).map(({ id, order }) => ({
            id,
            order,
          })),
          hiddenPromptLevels: [],
        };

      this.editProgramPromptLevels = this.normalizeEditProgramPromptLevels(promptLevelTemplate, baseEdit);
    },

    onReorderPromptLevel(promptLevel, direction, visible) {
      this.touched = true;

      const editPromptLevels = visible
        ? this.editProgramPromptLevels.visiblePromptLevels
        : this.editProgramPromptLevels.hiddenPromptLevels;

      const newPromptLevel = editPromptLevels.find(({ order }) => order === promptLevel.order);
      const swapPromptLevel = editPromptLevels.find(({ order }) => order === promptLevel.order + direction);

      if (!newPromptLevel || !swapPromptLevel) {
        return;
      }

      swapPromptLevel.order = newPromptLevel.order;
      newPromptLevel.order += direction;

      const newEditPromptLevels = editPromptLevels.reduce((acc, pl) => {
        if (pl.order === newPromptLevel.order) {
          acc.push(newPromptLevel);
        } else if (pl.order === swapPromptLevel.order) {
          acc.push(swapPromptLevel);
        } else {
          acc.push(pl);
        }
        return acc;
      }, []);

      if (visible) {
        this.editProgramPromptLevels.visiblePromptLevels = newEditPromptLevels;
      } else {
        this.editProgramPromptLevels.hiddenPromptLevels = newEditPromptLevels;
      }
    },

    onHidePromptLevel(promptLevel) {
      this.touched = true;

      const newVisiblePromptLevels = this.editProgramPromptLevels.visiblePromptLevels.reduce((acc, pl) => {
        if (pl.order === promptLevel.order) {
          return acc;
        }
        if (pl.order > promptLevel.order) {
          return [...acc, { ...pl, order: pl.order - 1 }];
        }
        return [...acc, pl];
      }, []);

      const maxHiddenOrder = this.editProgramPromptLevels.hiddenPromptLevels.reduce(
        (maxOrder, pl) => Math.max(maxOrder, pl.order),
        0,
      );

      this.editProgramPromptLevels.visiblePromptLevels = newVisiblePromptLevels;
      this.editProgramPromptLevels.hiddenPromptLevels = [
        ...this.editProgramPromptLevels.hiddenPromptLevels,
        {
          id: promptLevel.id,
          order: maxHiddenOrder + 1,
        },
      ];
    },

    onShowPromptLevel(promptLevel) {
      this.touched = true;

      const newHiddenPromptLevels = this.editProgramPromptLevels.hiddenPromptLevels.reduce((acc, pl) => {
        if (pl.order === promptLevel.order) {
          return acc;
        }
        if (pl.order > promptLevel.order) {
          return [...acc, { ...pl, order: pl.order - 1 }];
        }
        return [...acc, pl];
      }, []);

      const maxVisibleOrder = this.editProgramPromptLevels.visiblePromptLevels.reduce(
        (maxOrder, pl) => Math.max(maxOrder, pl.order),
        0,
      );

      this.editProgramPromptLevels.hiddenPromptLevels = newHiddenPromptLevels;
      this.editProgramPromptLevels.visiblePromptLevels = [
        ...this.editProgramPromptLevels.visiblePromptLevels,
        {
          id: promptLevel.id,
          order: maxVisibleOrder + 1,
        },
      ];
    },

    async onSave() {
      if (this.touched) {
        this.$emit('ok', this.editProgramPromptLevels);
      }

      this.hide();
    }, // onSave()

    getPromptLevelStyles(promptLevel) {
      const color = this.getPromptLevelTextColor(promptLevel);
      const bgColor = this.getPromptLevelColor(promptLevel);
      return `color:${color}; background-color:${bgColor}`;
    },

  },

};
</script>
<style lang="scss" scoped>
.promptlevel-editor {
  min-width:75vw;
}
</style>
